// Kampf am Ufer (§6), deterministisch: gleicher Seed, gleiche Aufstellung, gleicher Kampf (X2).
// Läuft in festen Takten. Alles, was passiert, landet im Ereignisprotokoll, das die Darstellung abspielt.
import {
  baseItemId,
  getAbility,
  combatRules as defaultRules,
  enemyKniff,
  classKniffe,
  defaultKniff,
  systemOn,
  getClass,
  getEnemy,
  getSpecies,
  getTrait,
  itemEffects,
  type CombatRules,
  type StormLevel,
} from '../content/index.ts';
import { createRng, type Rng } from './rng.ts';
import { FrameFlag, FrameFlag2 } from './types.ts';
import type {
  CombatProfile,
  EnemyPlacement,
  Side,
  BattleOptions,
  BattleResult,
  BattleSetup,
  Frame,
  FriendSummary,
  Outcome,
  Unit,
} from './types.ts';
import { distributeXp, xpPot } from './xp.ts';
export type { Battle } from './battle/core.ts';
import type { Battle } from './battle/core.ts';
import {
  EPS,
  param,
  optParam,
  perk,
  unitById,
  dist,
  gap,
  round,
  emit,
  targetable,
  exposedFriend,
  enemiesOf,
  nearest,
  setStatus,
  nextRetarget,
  speedFactor,
  moveToward,
  clampToField,
  rainMultiplierAt,
  duelMultiplier,
  dealDamage,
  shove,
  escape,
  heal,
  applyHeal,
  hitDamage,
  gainMut,
  attack,
  lowestHpEnemy,
  mostHurtFriend,
  placeNextTo,
  provoke,
} from './battle/core.ts';
import {
  applyCommands,
  orderTarget,
  preferClear,
  ready,
  updateDig,
  updateOrder,
  updateSlide,
  updateSwoop,
  updateTravel,
  updateWindup,
} from './battle/orders.ts';
import { launch, updateShots } from './battle/shots.ts';
import { inBush } from './battle/terrain.ts';
import {
  fireKniff,
  emerge,
  updateDive,
  updateSpin,
  updateRaid,
  updateGuardRun,
} from './battle/kniffe.ts';
import {
  taunted,
  updateSupport,
  updateFlitter,
  updateCrowd,
  updateBounce,
  updateHider,
  chooseEnemyTarget,
  chooseFriendTarget,
} from './battle/behavior.ts';

// ---------- Aufbau ----------

/** Prüft die Aufstellung gegen die Regeln, bevor gekämpft wird (K2, K4, K23, R25, K15). */
export function validateSetup(setup: BattleSetup, rules: CombatRules = defaultRules): void {
  const { fieldSlots, friends, enemies } = setup;
  // K4: 3 bis 10 Feldplätze, nicht mehr Freunde als Plätze
  if (fieldSlots < rules.fieldSlots.min || fieldSlots > rules.fieldSlots.max) throw new Error(`Feldplätze außerhalb ${rules.fieldSlots.min}–${rules.fieldSlots.max}: ${fieldSlots}`);
  if (friends.length === 0) throw new Error('Keine Freunde aufgestellt');
  if (friends.length > fieldSlots) throw new Error(`Mehr Freunde (${friends.length}) als Feldplätze (${fieldSlots})`);
  if (enemies.length === 0) throw new Error('Keine Gegner');
  const f = rules.field;
  // K2: freie Platzierung in der eigenen Hälfte
  for (const p of friends) {
    const s = getSpecies(p.species);
    const cls = p.friendClass ?? s.classes[0]!.friendClass;
    const entry = s.classes.find((c) => c.friendClass === cls);
    if (!entry) throw new Error(`${s.name} kann nicht ${cls} sein`);
    if (p.kniff && !classKniffe(entry).includes(p.kniff)) throw new Error(`${s.name} als ${cls} kennt ${p.kniff} nicht`);
    if (p.x < f.friendZone.xMin || p.x > f.friendZone.xMax || p.y < 0 || p.y > f.height) throw new Error(`${s.name} steht außerhalb der eigenen Hälfte`);
    // K27: Der Marsch darf nicht weiter gehen, als das Tier in der Zeit laufen kann
    if (p.march) {
      const speed = p.profile?.stats.moveSpeed ?? getClass(cls).stats.moveSpeed;
      if (Math.hypot(p.march.x - p.x, p.march.y - p.y) > marchReach(speed, rules) + 0.5) throw new Error(`${s.name} kann nicht so weit marschieren`);
    }
  }
  for (const e of enemies) {
    // E6: entweder ein Sturmwesen oder ein Befallener
    if (!!e.enemy === !!e.infested) throw new Error('Gegner braucht genau eins: enemy oder infested');
    if (e.enemy) getEnemy(e.enemy);
    if (e.infested) {
      const s = getSpecies(e.infested.species);
      if (!s.classes.some((c) => c.friendClass === e.infested!.friendClass)) throw new Error(`${s.name} kann nicht ${e.infested.friendClass} sein`);
    }
    const ts = e.traits ?? [];
    for (const id of ts) for (const x of getTrait(id).excludes) if (ts.includes(x)) throw new Error(`${id} und ${x} schließen sich aus`);
    if (e.x < f.enemyZone.xMin || e.x > f.enemyZone.xMax || e.y < 0 || e.y > f.height) throw new Error(`${e.enemy ?? e.infested!.species} steht außerhalb der Gegnerhälfte`);
  }
  if (!rules.stormLevels.some((l) => l.id === setup.stormLevel)) throw new Error(`Unbekannte Sturmstufe: ${setup.stormLevel}`);
  if (setup.retreat && !rules.retreatThresholds.some((r) => r.id === setup.retreat)) throw new Error(`Unbekannte Rückzugsschwelle: ${setup.retreat}`);
}

/** K27 (Fabi): Wie weit ein Tier zu Kampfbeginn marschieren kann, hängt von seinem Tempo ab [ZAHL?] */
export function marchReach(moveSpeed: number, rules: CombatRules = defaultRules): number {
  return moveSpeed * rules.march.seconds;
}

function blankUnit(uid: number): Unit {
  return {
    uid, side: 'friend', kind: 'friend', defId: '', name: '', friendClass: null,
    x: 0, y: 0, radius: 4, hp: 1, maxHp: 1, damage: 0, attackInterval: 1, range: 3, moveSpeed: 0, critChance: 0,
    attackCooldown: 0, mut: 0, mutPerAttack: 0, passive: null, kniff: null, attackHeal: 0,
    behavior: 'nearest', raid: null, hide: null, hideMelee: false, pounce: null, raidsStarted: 0, flit: null, weakAttacks: 0, weakFactor: 1, kniffTries: 0, confusedUntil: 0, weakKind: null, toxicUntil: 0, loot: [], traits: [], alive: true, targetUid: null, engagedUid: null,
    slowUntil: 0, slowFactor: 0, hasteUntil: 0, hasteFactor: 1, guardUntil: 0, guardReduction: 0, guardSource: null,
    tauntBy: null, tauntAttacks: 0, rolledUntil: 0, rollUsed: false, fleeUntil: 0, fleeUsed: false, escaped: false, stunnedUntil: 0,
    enragedUntil: 0, rageUsed: false, retargetAt: 0, marked: false, markedBy: null,
    poison: { stacks: 0, expiries: [], owners: [], nextTick: 0, source: null }, hots: [], dots: [], hitLog: [], critCount: 0, sporeUsed: false, amberUntil: 0, amberUsed: false, mutMax: 100, march: null, marchUntil: 0, edgeStrain: 0, pushedOut: false, climbUntil: 0, switchAt: 0,
    treeUid: null, homeX: 0, homeY: 0, onTree: false, leap: null,
    stats: { damage: 0, taken: 0, healed: 0, shielded: 0, kills: 0 },
    runId: null, startHp: 1, params: {}, perks: {}, perkFrom: {}, regenBank: 0, shield: 0, shieldSource: null, kniffUses: 0,
    spikyUntil: 0, spin: null, dive: null, markReservedBy: null, rushUntil: 0, burrowUntil: 0, empowerUntil: 0, empowerFactor: 1, items: [], level: 1,
    wounded: false, wardUid: null, guardRun: null, bond: null, paw: null,
    order: null, travel: null, digUntil: 0, slide: null, stuckUntil: 0, windup: null, markHold: false, markClaim: null, swoop: null,
  };
}

type AnimalSpec = { species: string; friendClass?: string; kniff?: string; name?: string; profile?: CombatProfile; id?: string; level?: number; wounded?: boolean };

/** Freund oder Befallener: Werte aus Klasse und Spezies (F1, F2), überschrieben vom Kampfprofil aus dem Run */
function addAnimal(b: { units: Unit[]; rng: Rng }, spec: AnimalSpec, side: Side, at: { x: number; y: number }, rules: CombatRules): Unit {
  const s = getSpecies(spec.species);
  const cls = spec.friendClass ?? s.classes[0]!.friendClass;
  const entry = s.classes.find((c) => c.friendClass === cls)!;
  const prof = spec.profile;
  const st = prof?.stats ?? getClass(cls).stats;
  const u = blankUnit(b.units.length);
  Object.assign(u, {
    side, kind: side === 'friend' ? 'friend' : 'infested', defId: s.id, name: spec.name ?? s.name, friendClass: cls,
    x: at.x, y: at.y, homeX: at.x, homeY: at.y, radius: s.radius, hp: st.hp, maxHp: st.hp, damage: st.damage,
    attackInterval: st.attackInterval, range: st.range, moveSpeed: st.moveSpeed * (prof ? 1 : (s.speed ?? 1)), critChance: st.critChance,
    mutPerAttack: rules.mut.perAttack, passive: prof?.passive ?? s.passive,
    kniff: prof?.kniff !== undefined ? prof.kniff : spec.kniff ?? defaultKniff(entry),
    attackHeal: prof?.attackHeal ?? entry.attackHeal ?? 0,
    params: { ...(entry.params ?? {}), ...(prof?.params ?? {}) }, perks: { ...(entry.perks ?? {}), ...(prof?.perks ?? {}) }, runId: spec.id ?? null, level: spec.level ?? 1,
    wounded: !!spec.wounded,
  });
  // Fabi (Phase Kern): Herkunft jeder Sonderwirkung; was die Klasse dieser Spezies mitbringt, heißt class:<spezies>:<klasse>
  u.perkFrom = prof?.perkFrom ? structuredClone(prof.perkFrom) : Object.fromEntries(Object.keys(entry.perks ?? {}).map((k) => [k, [`class:${s.id}:${cls}`]]));
  // R11: mitgenommene Verletzungen
  if (prof?.hp !== undefined) u.hp = Math.max(1, Math.min(u.maxHp, prof.hp));
  // K6 (Fabi): Wie viel Mut der Kniff braucht, hängt von Spezies und Klasse ab; Äste und Items können es ändern
  u.mutMax = rules.mut.max * (prof?.perks?.mutNeed ?? entry.mutNeed ?? 1);
  // K6 (Fabi): Start-Mut je nach Spezies und Klasse, höchstens ein Drittel
  u.mut = Math.min(rules.mut.startMax, prof?.perks?.startMut ?? entry.startMut ?? 0) * u.mutMax;
  // S10 Leibwache (Phase Kern): Zu Kampfbeginn ist er bereit, sonst käme er zu spät, wenn ein Windschnitt hinten hineinspringt
  if (u.kniff && optParam(u, u.kniff, 'readyAtStart') > 0) u.mut = u.mutMax;
  u.attackCooldown = b.rng.next() * u.attackInterval * 0.5;
  b.units.push(u);
  // S5: Das Eichhörnchen stellt seinen Baum mit auf und sitzt darauf, mit dem Leben, das der Baum noch hat
  if (s.passive === 'baumhaus') {
    const tree = blankUnit(b.units.length);
    const max = param(u, 'baumhaus', 'treeHp');
    const hp = prof?.treeHp !== undefined ? Math.min(max, prof.treeHp) : max;
    Object.assign(tree, {
      side, kind: 'tree', defId: 'baum', name: `Baum (${u.name})`,
      x: at.x, y: at.y, homeX: at.x, homeY: at.y, radius: param(u, 'baumhaus', 'treeRadius'), hp: Math.max(0, hp), maxHp: max,
    });
    b.units.push(tree);
    u.treeUid = tree.uid;
    // Ein gefällter Baum steht zu Kampfbeginn nicht (er wächst zwischen den Halten nach, R27)
    if (hp <= EPS) {
      tree.alive = false;
      tree.hp = 0;
    } else {
      u.onTree = true;
    }
  }
  return u;
}

/**
 * S13 (Erste Fassung): Passiven, die zu Kampfbeginn wirken. Schlau des Fuchses: mehr Volltreffer. Warnklatscher des Bibers: alle Freunde haben gleich etwas Mut.
 * Pfote halten des Otters: Er hält dem Freund, der ihm am nächsten steht, die Pfote; zwei Otter halten sich gegenseitig fest
 */
function startPassives(friends: Unit[]): void {
  // Fuchs, Schlau: findet im Kampf die Lücke
  for (const u of friends) if (u.passive === 'schlau') u.critChance = Math.min(1, u.critChance + optParam(u, 'schlau', 'critBonus'));
  for (const u of friends) {
    if (u.passive !== 'warnklatscher') continue;
    const share = optParam(u, 'warnklatscher', 'teamMut');
    for (const o of friends) o.mut = Math.min(o.mutMax, o.mut + share * o.mutMax);
  }
  for (const u of friends) {
    if (u.passive !== 'pfote-halten') continue;
    const mate = friends.filter((o) => o !== u).sort((a, c) => dist(a, u) - dist(c, u) || a.uid - c.uid)[0];
    if (!mate) continue;
    const reduction = optParam(u, 'pfote-halten', 'reduction');
    const reach = optParam(u, 'pfote-halten', 'reach');
    for (const [a, c] of [[u, mate], [mate, u]] as const) {
      a.paw ??= { with: [], reduction: 0, reach };
      if (!a.paw.with.includes(c.uid)) a.paw.with.push(c.uid);
      a.paw.reduction += reduction;
    }
  }
}

export function createBattle(setup: BattleSetup, seed: number, options: BattleOptions = {}, rules: CombatRules = defaultRules): Battle {
  validateSetup(setup, rules);
  const rng = createRng(seed);
  const level = rules.stormLevels.find((l) => l.id === setup.stormLevel)!;
  // R38: ohne Stärkung durch das Unwetter
  const storm = setup.calm ? { ...level, enemyBonus: 0, xpFactor: 1 } : level;
  const units: Unit[] = [];

  const ctx = { units, rng };
  for (const p of setup.friends) {
    const u = addAnimal(ctx, p, 'friend', p, rules);
    // K27: Wer auf seinem Baum sitzt, marschiert nicht
    // K33 (Phase Kern): Marschrichtung ausgeblendet, dann bleibt jeder zu Kampfbeginn stehen, wo er steht
    if (p.march && !u.onTree && systemOn('march')) startMarch(u, rules, p.march);
  }
  assignWards(units, setup);
  // B25: Bande aus dem Gemeinschaftsraum, von Indizes in friends auf uids
  const friendUnits = units.filter((u) => u.kind === 'friend');
  setup.friends.forEach((p, i) => {
    if (!p.bond || !friendUnits[i]) return;
    const others = p.bond.with.map((j) => friendUnits[j]?.uid).filter((x): x is number => x !== undefined && x !== friendUnits[i]!.uid);
    if (others.length) friendUnits[i]!.bond = { with: others, bonus: p.bond.bonus, reach: p.bond.reach };
  });
  startPassives(friendUnits);

  for (const p of setup.enemies) {
    if (p.infested) {
      // E6: Ein Befallener kämpft wie ein Freund, nur auf der anderen Seite; der Sturm stärkt ihn nicht
      const u = addAnimal(ctx, { ...p.infested, profile: { ...p.infested.profile } }, 'enemy', p, rules);
      u.traits = [...(p.traits ?? [])];
      u.items = carried(p);
      continue;
    }
    const u = blankUnit(units.length);
    Object.assign(u, stormlingStats(p, storm, rules));
    u.attackCooldown = rng.next() * u.attackInterval * 0.5;
    if (p.march && systemOn('march')) startMarch(u, rules, p.march);
    if (p.mutGain !== undefined) u.perks = { ...u.perks, mutGain: p.mutGain };
    if (p.kniff) u.kniff = p.kniff;
    units.push(u);
  }

  const battle: Battle = {
    rules, storm, setup, rng, t: 0, units, events: [], frames: [], recordFrames: !!options.recordFrames,
    rainMultiplier: 1,
    duelSince: null,
    nextGust: storm.gust?.every ?? Infinity,
    gust: null,
    gustAngle: null,
    nextLightning: storm.lightning?.every ?? Infinity,
    strikes: [],
    friendsStarted: units.filter((u) => u.kind === 'friend').length,
    farewells: 0,
    retreating: false,
    outcome: null,
    via: null,
    bounces: [],
    shots: [],
    nextShotId: 0,
    nextTelegraphId: 0,
    commands: [...(options.commands ?? [])].sort((a, c) => a.tick - c.tick),
  };
  // K46 Übungsufer: Leben aller mal einem Faktor, damit der Kampf lang genug für Züge ist
  const hpFactor = setup.hpFactor ?? 1;
  if (hpFactor !== 1) {
    for (const u of units) {
      u.maxHp *= hpFactor;
      u.hp *= hpFactor;
    }
  }
  // K49: Wer beim Aufstellen in einem Stein steht, rückt an dessen Rand
  if (setup.obstacles?.length) for (const u of units) if (u.kind !== 'tree') clampToField(battle, u);
  for (const u of units) u.startHp = u.hp;
  for (const u of units) if (u.traits.includes('unruhig')) u.retargetAt = nextRetarget(battle);
  recordFrame(battle);
  return battle;
}

/**
 * S10 Leibwache (Fabi, Phase Kern): Jeder Freund mit diesem Kniff bekommt seinen Schützling, den gewählten oder,
 * ohne Wahl, den Zerbrechlichsten (am wenigsten Höchstleben, bei Gleichstand der weiter hinten).
 */
export function assignWards(units: Unit[], setup: BattleSetup): void {
  const friends = units.filter((u) => u.kind === 'friend');
  friends.forEach((u, i) => {
    if (!u.kniff || getAbility(u.kniff).mechanic !== 'guardian') return;
    const chosen = setup.friends[i]?.ward;
    const pick = chosen !== undefined && chosen !== i ? friends[chosen] : undefined;
    u.wardUid = (pick ?? fragileOf(friends.filter((o) => o !== u)))?.uid ?? null;
  });
}

/** S10: der Zerbrechlichste unter den Freunden; dieselbe Wahl trifft die Darstellung beim Aufstellen */
export function fragileOf<T extends { maxHp: number; x: number }>(list: T[]): T | undefined {
  return list.reduce<T | undefined>((a, o) => (!a || o.maxHp < a.maxHp - 1e-6 || (Math.abs(o.maxHp - a.maxHp) <= 1e-6 && o.x < a.x) ? o : a), undefined);
}

/**
 * Ein Sturmwesen, wie es in den Kampf geht: Grundwerte, Sturmstufe und Tag (K21, R27), Würfel (G20), Eigenschaften (K23)
 * und getragene Items (G21). Die Darstellung zeigt beim Aufstellen dieselben Zahlen.
 */
export function stormlingStats(p: EnemyPlacement, storm: StormLevel, rules: CombatRules = defaultRules): Partial<Unit> {
  const e = getEnemy(p.enemy!);
  const traits = p.traits ?? [];
  const st = e.stats;
  const bonus = (1 + storm.enemyBonus) * (p.statFactor ?? 1);
  let hp = st.hp * bonus * (p.hpRoll ?? 1);
  let interval = st.attackInterval;
  if (traits.includes('zaeh')) hp *= getTrait('zaeh').params.hpFactor!;
  if (traits.includes('traege')) interval *= getTrait('traege').params.attackIntervalFactor!;
  // K23 Hastig (Fabi, Runde 7): bewegt sich etwas schneller
  const speed = st.moveSpeed * (traits.includes('hastig') ? getTrait('hastig').params.moveSpeedFactor! : 1);
  const u = blankUnit(0);
  Object.assign(u, {
    side: 'enemy', kind: 'stormling', defId: e.id, name: e.name, x: p.x, y: p.y, homeX: p.x, homeY: p.y, items: carried(p),
    radius: st.radius, hp, maxHp: hp, damage: st.damage * bonus * (p.damageRoll ?? 1), attackInterval: interval, range: st.range, mutMax: rules.mut.max * (e.mutNeed ?? 1),
    moveSpeed: speed, mutPerAttack: e.mutPerAttack ?? rules.mut.perAttack, kniff: enemyKniff(e),
    critChance: st.critChance + (traits.includes('tueckisch') ? getTrait('tueckisch').params.critChanceBonus! : 0),
    behavior: e.behavior, traits: [...traits], hide: e.hide ?? null,
  });
  for (const ref of u.items) applyItemToStormling(u, ref);
  const { uid: _uid, ...rest } = u;
  return rest;
}

/** I18: alles, was ein Gegner trägt */
const carried = (p: EnemyPlacement): string[] => [...(p.item ? [p.item] : []), ...(p.items ?? [])];

/** G21: Werte und Sonderwirkungen eines Items auf ein Sturmwesen übertragen */
function applyItemToStormling(u: Unit, ref: string): void {
  for (const e of itemEffects(ref)) {
    if ('stat' in e) {
      const mul = e.mul ?? 1;
      const add = e.add ?? 0;
      if (e.stat === 'hp') {
        u.maxHp = u.maxHp * mul + add;
        u.hp = u.maxHp;
      } else if (e.stat === 'damage') u.damage = u.damage * mul + add;
      else if (e.stat === 'attackSpeed') u.attackInterval = u.attackInterval / mul;
      else if (e.stat === 'critChance') u.critChance += add;
      else if (e.stat === 'moveSpeed') u.moveSpeed = u.moveSpeed * mul + add;
    } else if ('perk' in e && e.perk !== 'revive') {
      u.perks[e.perk] = (u.perks[e.perk] ?? 0) + e.value;
      (u.perkFrom[e.perk] ??= []).push(`item:${baseItemId(ref)}`);
    }
  }
}

// ---------- Takt ----------

function updateTimers(b: Battle, u: Unit): void {
  // F32 (Fabi, Spieltest Phase 4): Gift tickt alle halbe Sekunde, unabhängig von Angriffen und auch dann weiter,
  // wenn die Maus schon fort ist; pro Sekunde bleibt der Schaden gleich [ZAHL?]
  if (u.poison.stacks > 0) {
    if (b.t + EPS >= u.poison.nextTick) {
      const tick = b.rules.poisonTickSeconds;
      u.poison.nextTick += tick;
      // F32 (Fabi, Spieltest Phase 4): Gegen Tanks soll die Giftmaus glänzen, darum frisst jeder Stapel auch einen
      // kleinen Anteil vom Leben des Ziels [ZAHL?]
      // Fabi (Phase Kern): je Vergifter getrennt, damit jeder seinen Anteil im Verlauf sieht und dafür XP bekommt
      const counts = new Map<number, number>();
      for (const o of u.poison.owners) counts.set(o, (counts.get(o) ?? 0) + 1);
      for (const [owner, n] of [...counts].sort((x, y) => x[0] - y[0])) {
        const src = unitById(b, owner);
        const perStack = param(src, 'giftiger-biss', 'damagePerStackPerSecond') + optParam(src, 'giftiger-biss', 'maxHpSharePerStack') * u.maxHp;
        // F44 Brennendes Gift (Fabi, Phase Kern): die Stapel des Feuersalamanders brennen stärker, dafür kürzer
        const hot = src?.perks.poisonBurn ?? 1;
        dealDamage(b, src ?? null, u, n * perStack * hot * tick, 'poison');
        if (!u.alive) break;
      }
    }
    const keep = u.poison.expiries.map((e) => e > b.t + EPS);
    u.poison.expiries = u.poison.expiries.filter((_, i) => keep[i]);
    u.poison.owners = u.poison.owners.filter((_, i) => keep[i]);
    u.poison.stacks = u.poison.expiries.length;
  }
  // S8 Nachwachsen (Runde 7): Der Feuersalamander erholt sich im Kampf ständig ein wenig
  if (u.passive === 'nachwachsen' && u.alive && u.hp < u.maxHp) {
    const before = u.hp;
    u.hp = Math.min(u.maxHp, u.hp + u.maxHp * param(u, 'nachwachsen', 'regenPerSecond') * b.rules.tickSeconds);
    u.regenBank += u.hp - before;
  }
  // Fabi (Phase Kern): Die Erholung steht einmal pro Sekunde im Verlauf, damit sichtbar ist, was auf ihn wirkt
  if (u.regenBank > 0 && Math.floor(b.t + EPS) > Math.floor(b.t - b.rules.tickSeconds + EPS)) {
    if (u.regenBank >= 0.5) emit(b, { t: round(b.t), type: 'heal', from: u.uid, to: u.uid, amount: round(u.regenBank), via: 'passive:nachwachsen' });
    u.regenBank = 0;
  }
  // K21: Wer nicht mehr gegen den Rand gedrückt wird, fasst wieder Fuß
  if (u.edgeStrain > 0) u.edgeStrain = Math.max(0, u.edgeStrain - b.rules.edge.recoverPerSecond * b.rules.tickSeconds);
  // Nachwirkende Heilung (Gründlich)
  for (const h of u.hots) {
    if (!u.alive || h.ticksLeft <= 0 || b.t + EPS < h.nextTick) continue;
    h.ticksLeft -= 1;
    h.nextTick += 1;
    const src = unitById(b, h.source);
    if (src) applyHeal(b, src, u, h.perTick, h.via);
  }
  u.hots = u.hots.filter((h) => h.ticksLeft > 0);
  // Q49 Gründlich: Schaden, der nachwirkt
  for (const d of u.dots) {
    if (!u.alive || d.ticksLeft <= 0 || b.t + EPS < d.nextTick) continue;
    d.ticksLeft -= 1;
    d.nextTick += 1;
    // I25: Gift von Fingerhut und Bienenstich zählt als Gift, nicht als Nachwirken
    dealDamage(b, unitById(b, d.source) ?? null, u, d.perTick, d.venom ? 'poison' : 'dot', d.via);
  }
  u.dots = u.dots.filter((d) => d.ticksLeft > 0);
  // K51: Ist der Kniff vorbei, der die Schwachstelle getroffen hat, ist auch die Zielscheibe weg
  if (u.markClaim && b.t > u.markClaim.until + EPS) {
    u.marked = false;
    u.markedBy = null;
    u.markHold = false;
    u.markClaim = null;
  }
  // F35 Raserei (Fabi): Je weniger Leben die Maus hat, desto schneller greift sie an
  const fury = perk(u, 'lowHpHaste') > 0 ? 1 + perk(u, 'lowHpHaste') * (1 - u.hp / u.maxHp) : 1;
  if (u.attackCooldown > 0) u.attackCooldown -= b.rules.tickSeconds * speedFactor(b, u) * fury;
}

/** S5 Eichhörnchen: auf dem Baum, im Sprung oder ohne Baum */
function leapHome(b: Battle, u: Unit): void {
  // Zurück an den Baum, oder dahin, wo er stand
  u.leap = null;
  u.x = u.homeX;
  u.y = u.homeY;
  const tree = unitById(b, u.treeUid);
  u.onTree = !!tree && tree.alive;
  u.targetUid = null;
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'return' });
}

function updateSquirrel(b: Battle, u: Unit): boolean {
  // S5 (Fabi, Spieltest Phase 4): vom Baum gepustet; solange es wieder hochklettert, greift es nicht an und heilt nicht
  if (u.climbUntil > 0) {
    const tree = unitById(b, u.treeUid);
    if (!tree || !tree.alive) {
      u.climbUntil = 0;
      return false;
    }
    if (b.t + EPS < u.climbUntil) {
      moveToward(b, u, tree.x, tree.y);
      return true;
    }
    u.climbUntil = 0;
    u.x = tree.x;
    u.y = tree.y;
    u.onTree = true;
    emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'return' });
    return true;
  }
  if (u.leap) {
    // Q34 (Fabi, Variante a): nach den Treffern eine Weile beim Ziel stehen, angreifbar
    if (u.leap.lingerUntil !== undefined) {
      if (b.t + EPS >= u.leap.lingerUntil) leapHome(b, u);
      return true;
    }
    if (b.t + EPS < u.leap.nextHitAt) return true;
    let victim = unitById(b, u.leap.targetUid);
    // F35 Wirbelwind: reihum durch die schwächsten Gegner; wer schon gefallen ist, wird übersprungen
    const cycle = u.leap.cycle;
    if (cycle && cycle.length) {
      const done = param(u, 'nussknacker', 'hits') - u.leap.hitsLeft;
      for (let i = 0; i < cycle.length; i++) {
        const next = unitById(b, cycle[(done + i) % cycle.length]!);
        if (next && targetable(b, next)) {
          if (next.uid !== u.leap.targetUid) {
            placeNextTo(b, u, next);
            u.leap.targetUid = next.uid;
            emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
          }
          victim = next;
          break;
        }
      }
    }
    if (!victim || !targetable(b, victim)) {
      victim = lowestHpEnemy(b, u);
      if (victim) {
        placeNextTo(b, u, victim);
        u.leap.targetUid = victim.uid;
      }
    }
    if (victim) {
      dealDamage(b, u, victim, hitDamage(b, u, param(u, 'nussknacker', 'damageFactor')), 'kniff');
      // K36 (Fabi, Phase Kern): Wer vom Baum direkt vor einem Gegner landet und ihn trifft, zieht ihn an
      provoke(b, u, victim);
    }
    // Der Treffer kann das Eichhörnchen selbst zu Fall bringen (I5 Moorbeere, F35 Stachelkugel)
    if (!u.leap) return true;
    u.leap.hitsLeft -= 1;
    u.leap.nextHitAt = b.t + param(u, 'nussknacker', 'hitInterval');
    if (!victim) leapHome(b, u);
    else if (u.leap.hitsLeft <= 0) u.leap.lingerUntil = b.t + param(u, 'nussknacker', 'lingerSeconds');
    return true;
  }
  if (u.onTree) {
    // Auf dem Baum: ein wenig Erholung, Mut füllt sich über eine feste Dauer
    u.hp = Math.min(u.maxHp, u.hp + u.maxHp * param(u, 'baumhaus', 'regenPerSecond') * b.rules.tickSeconds);
    gainMut(b, u, (u.mutMax / param(u, 'baumhaus', 'mutFillSeconds')) * b.rules.tickSeconds);
    if (u.mut >= u.mutMax - EPS && mayAutoKniff(b, u)) fireKniff(b, u);
    // R27, Q45 (Fabi): Das Eichhörnchen als Beschützer wirft vom Baum Nüsse auf Gegner, ein bisschen Schaden,
    // damit es nicht untätig wartet. Die Würfe füllen den Mut nicht, das tut der Baum.
    if (perk(u, 'treeThrow') > 0 && u.attackCooldown <= EPS) {
      const tgt = nearest(u, enemiesOf(b, u).filter((o) => o.kind !== 'tree' && gap(u, o) <= u.range + EPS));
      if (tgt) {
        attack(b, u, tgt, { mut: false });
        u.attackCooldown += u.attackInterval;
      }
    }
    return true;
  }
  return false; // ohne Baum kämpft es wie alle anderen
}

/** K46: Im commandMode zünden die Kniffe der Freunde nie von selbst; sie warten mit vollem Mut auf den Befehl */
const mayAutoKniff = (b: Battle, u: Unit): boolean => !(b.setup.commandMode && u.side === 'friend' && !b.setup.autoKniffe);

function updateUnit(b: Battle, u: Unit): void {
  if (!u.alive || u.kind === 'tree') return;
  if (u.stunnedUntil > b.t) return; // K21: vom Blitz benommen
  // K50: schlittert in die Mulde; K46: unterwegs auf eigene Art; K50: gräbt seinen Bau; K52: im Sturzflug
  if (u.slide && updateSlide(b, u)) return;
  if (u.travel && updateTravel(b, u)) return;
  if (u.digUntil > 0 && updateDig(b, u)) return;
  if (u.swoop && updateSwoop(b, u)) return;
  // K48: holt aus für einen angesagten Angriff
  if (u.windup && updateWindup(b, u)) return;
  // K15: Rückzug, alle laufen zum eigenen Rand und greifen nicht mehr an
  if (u.side === 'friend' && b.retreating) {
    if (u.rolledUntil > b.t) return;
    moveToward(b, u, 0, u.y, b.rules.retreat.fleeSpeed);
    if (u.x <= u.radius + 0.5) escape(b, u);
    return;
  }
  if (u.march && updateMarch(b, u)) return;
  // S10 Leibwache: unterwegs zum Schützling
  if (u.guardRun && updateGuardRun(b, u)) return;
  if (u.passive === 'baumhaus' && updateSquirrel(b, u)) return;
  // F35 (Fabi): Rollt sich der Igel beim Kreiseln ein (Passive), kreiselt er weiter; die Passive bricht den Kniff nicht ab
  if (u.spin && u.rolledUntil > b.t && updateSpin(b, u)) return;
  if (u.rolledUntil > b.t || u.spikyUntil > b.t) return; // eingerollt
  if (u.burrowUntil > 0) {
    if (b.t + EPS < u.burrowUntil) return; // unter der Erde
    emerge(b, u);
    return;
  }
  if (updateSpin(b, u)) return; // F35 Stachelball
  if (updateDive(b, u)) return; // R27 Stoßtauchen
  // K46: Befehle gehen dem eigenen Handeln vor
  if (u.order && updateOrder(b, u, fireKniff)) return;

  // K23 Feige: zieht sich langsam zum eigenen Rand zurück; wer ihn erreicht, ist entkommen
  if (u.fleeUntil > b.t) {
    moveToward(b, u, b.rules.field.width, u.y, getTrait('feige').params.fleeSpeed!);
    if (u.x >= b.rules.field.width - u.radius - 0.5) escape(b, u);
    return;
  }

  if (updateRaid(b, u)) return;
  if (updateHider(b, u)) return; // G26 Nieselchen
  // Runde 7 (Fabi): neue Sturmwesen mit eigenem Verhalten
  if (updateSupport(b, u) || updateFlitter(b, u) || updateCrowd(b, u) || updateBounce(b, u)) return;
  if (u.mut >= u.mutMax - EPS && mayAutoKniff(b, u) && fireKniff(b, u)) return;

  // K46: Ein Angriffsbefehl gilt diesem Ziel; muss es gerade warten (eingegraben), wartet er
  const ordered = orderTarget(b, u);
  if (ordered === null) {
    u.targetUid = null;
    return;
  }

  // S5 Waschbär: Angriff heilt den, dem prozentual am meisten fehlt
  if (u.attackHeal > 0 && !ordered) {
    const patient = mostHurtFriend(b, u, true);
    if (patient) {
      u.targetUid = patient.uid;
      if (gap(u, patient) > u.range) {
        moveToward(b, u, patient.x, patient.y);
      } else if (u.attackCooldown <= EPS) {
        emit(b, { t: round(b.t), type: 'strike', unit: u.uid, target: patient.uid, kind: 'heal' });
        // K47: Auch das Heilmoos fliegt; Heilung kommt immer an
        if (b.setup.projectiles) launch(b, u, patient, { kind: 'heal', amount: u.attackHeal * b.rainMultiplier }, b.rules.projectiles.heal);
        else heal(b, u, patient, u.attackHeal * b.rainMultiplier);
        gainMut(b, u, u.mutPerAttack);
        u.attackCooldown += u.attackInterval;
      }
      return;
    }
  }

  const chosen = ordered ?? (u.side === 'enemy' ? chooseEnemyTarget(b, u) : chooseFriendTarget(b, u));
  // K49: Fernkämpfer zielen nicht ins Gebüsch und lieber auf jemanden mit freier Bahn
  const tgt = ordered ? ordered : preferClear(b, u, chosen);
  u.targetUid = tgt?.uid ?? null;
  if (!tgt) return;
  // K44 (Fabi, Phase Kern, 17.10.2026 nachts): Nahkämpfer stellen sich links oder rechts neben ihr Ziel, nicht darüber
  // oder darunter, damit der Schlag im Bild das Ziel trifft und nicht ins Leere geht
  const dock = meleeDock(b, u, tgt);
  // K47: Ein Fernkämpfer der Sturmwesen zielt um seine Kumpel herum und tritt dafür zur Seite; wer ihm danach in die Bahn
  // läuft, wird trotzdem getroffen
  if (b.setup.projectiles && u.side === 'enemy' && u.range >= 10 && gap(u, tgt) <= u.range && sidestep(b, u, tgt)) return;
  if (gap(u, tgt) > u.range) {
    moveToward(b, u, dock?.x ?? tgt.x, dock?.y ?? tgt.y);
    return;
  }
  // Fanatisch wartet vor einem Ziel, das man gerade nicht angreifen kann
  if (!targetable(b, tgt)) return;
  if (u.attackCooldown <= EPS) {
    attack(b, u, strikeTarget(b, u, tgt));
    u.attackCooldown += u.attackInterval;
    if (u.mut >= u.mutMax - EPS && mayAutoKniff(b, u)) fireKniff(b, u);
  }
}

/** K47: Steht ein Kumpel in der Schusslinie, ein Schritt zur Seite; true, wenn er dafür diesen Takt braucht */
function sidestep(b: Battle, u: Unit, tgt: Unit): boolean {
  const dx = tgt.x - u.x;
  const dy = tgt.y - u.y;
  const len = Math.hypot(dx, dy);
  if (len < EPS) return false;
  for (const o of b.units) {
    if (o === u || o.side !== u.side || !o.alive || o.kind === 'tree') continue;
    const k = ((o.x - u.x) * dx + (o.y - u.y) * dy) / (len * len);
    if (k <= 0 || k >= 1) continue;
    const across = ((o.x - u.x) * dy - (o.y - u.y) * dx) / len;
    if (Math.abs(across) > o.radius + 0.8) continue;
    // weg von der Seite, auf der der Kumpel steht
    const side = across >= 0 ? -1 : 1;
    moveToward(b, u, u.x + (dy / len) * side * 6, u.y - (dx / len) * side * 6);
    return true;
  }
  return false;
}

/** K44: der Platz links oder rechts neben dem Ziel, auf der Seite, von der man kommt; null für Fernkämpfer */
function meleeDock(b: Battle, u: Unit, tgt: Unit): { x: number; y: number } | null {
  if (u.range >= 10 || tgt.kind === 'tree') return null;
  const side = u.x <= tgt.x ? -1 : 1;
  const x = tgt.x + side * (u.radius + tgt.radius + Math.min(u.range, 2) * 0.5);
  return { x: Math.min(b.rules.field.width - u.radius, Math.max(u.radius, x)), y: tgt.y };
}

/**
 * I25 (Fabi, Runde 7): Pusteblume trifft bei jedem Angriff einen zufälligen Gegner in Reichweite, Bussardkralle den
 * schwächsten. Beide wählen nur unter denen, die schon in Reichweite sind; so läuft niemand kopflos übers Ufer.
 */
function strikeTarget(b: Battle, u: Unit, tgt: Unit): Unit {
  const random = perk(u, 'randomTarget') > 0;
  const weakest = perk(u, 'weakestTarget') > 0;
  if (!random && !weakest) return tgt;
  const near = b.units.filter((o) => o.side !== u.side && o.kind !== 'tree' && targetable(b, o) && gap(u, o) <= u.range + EPS);
  if (near.length < 2) return tgt;
  if (random) return near[Math.floor(b.rng.next() * near.length)]!;
  return near.reduce((a, o) => (o.hp < a.hp ? o : a));
}

/**
 * K27 (Fabi, Spieltest Phase 4): Zu Kampfbeginn läuft, wer eine Marschrichtung hat, bis zu ihrem Ende. Trifft er
 * unterwegs auf einen Gegner, greift er den an; sonst sucht er sich am Ziel wie alle den Nächsten.
 */
function updateMarch(b: Battle, u: Unit): boolean {
  const m = u.march!;
  // Spieltest Phase 4 (Fabi): Wer direkt an einem Gegner vorbeiläuft, greift ihn an, statt stumpf weiterzulaufen.
  // Und niemand bleibt hängen: Nach der Marschzeit (mit etwas Luft) ist der Marsch auf jeden Fall vorbei.
  if (b.t + EPS >= u.marchUntil) {
    u.march = null;
    return false;
  }
  const touching = nearest(u, b.units.filter((o) => o.side !== u.side && o.alive && o.kind !== 'tree' && targetable(b, o) && gap(u, o) <= b.rules.march.engageGap + EPS));
  if (touching) {
    u.march = null;
    u.targetUid = touching.uid;
    u.engagedUid = touching.uid;
    return false;
  }
  if (Math.hypot(m.x - u.x, m.y - u.y) <= 0.5) {
    u.march = null;
    return false;
  }
  moveToward(b, u, m.x, m.y);
  return true;
}

/** K27: Marschziel so, dass der Kreis es auch erreicht (nicht halb über dem Rand), und eine Zeitgrenze */
function startMarch(u: Unit, rules: CombatRules, p: { x: number; y: number }): void {
  const r = u.radius + 0.5;
  u.march = { x: Math.min(rules.field.width - r, Math.max(r, p.x)), y: Math.min(rules.field.height - r, Math.max(r, p.y)) };
  u.marchUntil = rules.march.seconds * 1.6;
}

/**
 * Kreise schieben sich gegenseitig weg, statt sich zu überlappen (K20). Bäume stehen fest. K40 (Fabi, Phase Kern,
 * 17.10.2026): Freunde blocken sich nicht mehr, wer nach vorn will, geht am anderen vorbei; wer sich zu nah kommt, rückt
 * sanft zur Seite, vor allem nach oben oder unten, damit keiner im anderen steht.
 */
function separate(b: Battle): void {
  // K46: Wer unter der Erde wühlt, herbeigewinkt wird oder im Sturzflug ist, schiebt niemanden
  const solid = b.units.filter((u) => u.alive && !u.onTree && !u.travel?.safe && !u.swoop);
  for (let i = 0; i < solid.length; i++) {
    for (let j = i + 1; j < solid.length; j++) {
      const a = solid[i]!;
      const c = solid[j]!;
      const d = dist(a, c);
      const mates = a.side === 'friend' && c.side === 'friend' && a.kind !== 'tree' && c.kind !== 'tree';
      const reach = (a.radius + c.radius) * (mates ? b.rules.friendOverlap.reach : 1);
      const overlap = (reach - d) * (mates ? b.rules.friendOverlap.push : 1);
      if (overlap <= 0) continue;
      let nx = d > EPS ? (c.x - a.x) / d : 1;
      let ny = d > EPS ? (c.y - a.y) / d : 0;
      if (mates) {
        // K44 (Fabi, 17.10.2026 nachts): nie blocken, aber auch nie ineinander stehen. Freunde weichen vor allem nach oben oder
        // unten aus, so geht der hintere seitlich am vorderen vorbei, statt hinter ihm festzustecken
        const side = Math.abs(ny) < 0.25 ? (a.uid < c.uid ? 1 : -1) * 0.8 : 0;
        const lx = nx * 0.35;
        const ly = ny + side;
        const l = Math.hypot(lx, ly) || 1;
        nx = lx / l;
        ny = ly / l;
      }
      const aFixed = a.kind === 'tree' || !!a.leap;
      const cFixed = c.kind === 'tree' || !!c.leap;
      if (aFixed && cFixed) continue;
      const shareA = aFixed ? 0 : cFixed ? 1 : 0.5;
      a.x -= nx * overlap * shareA;
      a.y -= ny * overlap * shareA;
      c.x += nx * overlap * (1 - shareA);
      c.y += ny * overlap * (1 - shareA);
    }
  }
  for (const u of solid) clampToField(b, u);
}

/** K9/K21: Böen schieben die Freunde; Blitze schlagen zufällig ein (Fabi, Phase 3) */
function stormChaos(b: Battle): void {
  // K33 (Phase Kern): Wind und Blitze lassen sich ausblenden (data/switches.json)
  const gust = systemOn('wind') ? b.storm.gust : null;
  const lightning = systemOn('lightning') ? b.storm.lightning : null;
  if (gust && b.t + EPS >= b.nextGust) {
    b.nextGust += gust.every;
    // K21 (Fabi, Spieltest Phase 4): Der Wind dreht von Böe zu Böe ein Stück, damit er nicht alle stur nach rechts schiebt
    b.gustAngle = b.gustAngle === null ? b.rng.next() * Math.PI * 2 : b.gustAngle + (b.rng.next() - 0.5) * b.rules.edge.gustTurn * 2;
    const angle = b.gustAngle;
    // K32 (Fabi, Runde 8): Dauer und Stärke der Böe hängen von der Sturmstufe ab
    const speed = gust.push / gust.duration;
    b.gust = { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, until: b.t + gust.duration };
    emit(b, { t: round(b.t), type: 'gust', dx: round(Math.cos(angle) * gust.push), dy: round(Math.sin(angle) * gust.push), duration: gust.duration });
    // S5 (Fabi): Eine Böe kann das Eichhörnchen vom Baum pusten; dann muss es erst wieder hochklettern
    for (const u of b.units) {
      if (!u.alive || !u.onTree || u.kind !== 'friend') continue;
      if (b.rng.next() >= b.rules.edge.treeBlowChance * (gust.push / 20)) continue;
      u.onTree = false;
      u.climbUntil = b.t + b.rules.edge.climbSeconds;
      shove(b, u, Math.cos(angle) * 4, Math.sin(angle) * 4);
      setStatus(b, u, 'climbing', u.climbUntil, 'storm:wind');
    }
  }
  if (b.gust) {
    if (b.t + EPS >= b.gust.until) b.gust = null;
    else {
      for (const u of b.units) {
        if (!exposedFriend(u) || u.leap) continue;
        // Leichte Tiere fliegen weiter als schwere (Größe als Maß fürs Gewicht, Q62: gemessen an einem Dachs, früher an
        // einem Kreis von 4); I26 Wurzelwerk: alle stehen fester. Der Wind ist aus; beim Wiedereinschalten neu einstellen
        const w = (b.rules.edge.pushRadius / u.radius) ** 2 * (b.setup.mods?.gustFactor ?? 1);
        shove(b, u, b.gust.vx * w * b.rules.tickSeconds, b.gust.vy * w * b.rules.tickSeconds);
      }
    }
  }
  if (lightning && b.t + EPS >= b.nextLightning) {
    b.nextLightning += lightning.every;
    // Zufälliger Punkt dort, wo gerade gekämpft wird
    const fighting = b.units.filter((u) => u.alive && u.kind !== 'tree' && !u.onTree);
    if (fighting.length > 0) {
      const pad = 6;
      const f = b.rules.field;
      const minX = Math.max(0, Math.min(...fighting.map((u) => u.x)) - pad);
      const maxX = Math.min(f.width, Math.max(...fighting.map((u) => u.x)) + pad);
      const minY = Math.max(0, Math.min(...fighting.map((u) => u.y)) - pad);
      const maxY = Math.min(f.height, Math.max(...fighting.map((u) => u.y)) + pad);
      for (let i = 0; i < lightning.count; i++) {
        const x = minX + b.rng.next() * (maxX - minX);
        const y = minY + b.rng.next() * (maxY - minY);
        // P4: Ein Schatten warnt vor dem Einschlag
        const strike = { x, y, radius: lightning.radius, damage: lightning.damage, at: b.t + lightning.warning };
        b.strikes.push(strike);
        emit(b, { t: round(b.t), type: 'lightningWarning', x: round(x), y: round(y), radius: strike.radius, strikeAt: round(strike.at) });
      }
    }
  }
  const due = b.strikes.filter((s) => b.t + EPS >= s.at);
  b.strikes = b.strikes.filter((s) => b.t + EPS < s.at);
  for (const s of due) {
    emit(b, { t: round(b.t), type: 'lightning', x: round(s.x), y: round(s.y), radius: s.radius });
    for (const u of b.units) {
      if (!u.alive || u.kind === 'tree' || u.onTree || dist(u, s) > s.radius + u.radius + EPS) continue;
      const rod = b.setup.mods?.lightningRod;
      if (u.side === 'friend' && rod) {
        // I26 Blitzfänger (Fabi, Runde 7): Der Blitz tut nichts, er lädt auf; etwa zwei Angriffe lang geht es viel schneller
        u.hasteFactor = u.hasteUntil > b.t ? Math.max(u.hasteFactor, rod.factor) : rod.factor;
        u.hasteUntil = Math.max(u.hasteUntil, b.t + (rod.attacks * u.attackInterval) / rod.factor);
        setStatus(b, u, 'hasted', u.hasteUntil, 'artifact:blitzfaenger');
      } else if (u.side === 'friend') {
        // K33 (Fabi, Phase Kern): erst einmal ohne Schaden, dafür länger benommen
        if (systemOn('lightningDamage')) dealDamage(b, null, u, s.damage, 'lightning', 'storm:lightning');
        if (u.alive && b.rules.lightningStun > 0) {
          u.stunnedUntil = b.t + b.rules.lightningStun;
          setStatus(b, u, 'stunned', u.stunnedUntil, 'storm:lightning');
        }
      } else {
        // K21: Das Gewitter stärkt die Sturmwesen, getroffene greifen eine Weile schneller an
        const h = b.rules.lightningEnemyHaste;
        u.hasteFactor = u.hasteUntil > b.t ? Math.max(u.hasteFactor, h.factor) : h.factor;
        u.hasteUntil = Math.max(u.hasteUntil, b.t + h.duration);
        setStatus(b, u, 'hasted', u.hasteUntil, 'storm:lightning');
      }
    }
  }
}

/**
 * Anteil des verbliebenen Lebens einer Seite, Gefallene und Entkommene zählen mit 0.
 * R27/K15: gemessen am Leben, mit dem jemand ans Ufer kam, sonst zöge sich ein angeschlagener Landtrupp sofort zurück.
 */
function lifeShare(b: Battle, side: 'friend' | 'enemy'): number {
  let hp = 0;
  let max = 0;
  for (const u of b.units) {
    if (u.side !== side || u.kind === 'tree') continue;
    max += u.startHp;
    if (u.alive) hp += u.hp;
  }
  return max > 0 ? Math.min(1, hp / max) : 0;
}

/**
 * K15 (Fabi, Phase 3): Der Landtrupp zieht sich gemeinsam zurück.
 * Auslöser: Die Gegner haben prozentual um so viele Punkte mehr Leben übrig als die Freunde.
 */
function shouldRetreat(b: Battle): boolean {
  const id = b.setup.retreat ?? b.rules.retreatThresholds.find((r) => r.default)!.id;
  const th = b.rules.retreatThresholds.find((r) => r.id === id)!;
  if (th.advantageAtLeast === undefined) return false; // bis zum Letzten
  // Verhältnis, nicht Abstand: „Gegner haben 50 % mehr Leben übrig als wir“ heißt Faktor 1,5
  const friends = lifeShare(b, 'friend');
  return lifeShare(b, 'enemy') >= friends * (1 + th.advantageAtLeast) - EPS;
}

function startRetreat(b: Battle): void {
  b.retreating = true;
  emit(b, { t: round(b.t), type: 'retreat' });
  for (const u of b.units) {
    if (u.side !== 'friend' || !u.alive || u.kind === 'tree') continue;
    u.onTree = false; // auch das Eichhörnchen kommt mit, der Baum bleibt (K5)
    u.leap = null;
    u.guardRun = null;
    u.fleeUntil = Infinity;
    setStatus(b, u, 'retreating', b.t);
  }
  for (const e of b.units) if (e.side === 'enemy') e.tauntBy = null;
}

function checkEnd(b: Battle): void {
  const enemiesLeft = b.units.some((u) => u.side === 'enemy' && u.alive);
  const standing = b.units.filter((u) => u.kind === 'friend' && u.alive).length;
  const safe = b.units.filter((u) => u.kind === 'friend' && u.escaped && !u.pushedOut).length;
  let outcome: Outcome | null = null;
  let reason: 'allEnemiesDown' | 'allFriendsDown' | 'retreat' | 'timeout' = 'timeout';
  if (!enemiesLeft && !b.retreating) [outcome, reason] = ['win', 'allEnemiesDown'];
  else if (standing === 0) [outcome, reason] = safe > 0 ? ['retreat', 'retreat'] : ['loss', 'allFriendsDown'];
  else if (!b.retreating && shouldRetreat(b)) startRetreat(b);
  else if (b.t + EPS >= b.rules.maxSeconds) [outcome, reason] = ['loss', 'timeout'];
  if (outcome) {
    b.outcome = outcome;
    emit(b, { t: round(b.t), type: 'end', outcome, reason });
  }
}

function flagsOf(b: Battle, u: Unit): number {
  const F = FrameFlag;
  let f = 0;
  if (u.alive) f |= F.alive;
  if (u.onTree) f |= F.onTree;
  if (u.rolledUntil > b.t) f |= F.rolled;
  if (u.guardUntil > b.t) f |= F.guarded;
  if (u.marked) f |= F.marked;
  if (u.fleeUntil > b.t) f |= F.fleeing;
  if (u.stunnedUntil > b.t) f |= F.stunned;
  if (u.enragedUntil > b.t) f |= F.enraged;
  if (u.hasteUntil > b.t) f |= F.hasted;
  if (u.slowUntil > b.t) f |= F.slowed;
  if (u.poison.stacks > 0 || u.dots.some((d) => d.venom)) f |= F.poisoned;
  if (u.tauntBy !== null && u.tauntAttacks > 0) f |= F.taunted;
  if (u.leap) f |= F.leaping;
  if (u.spin && u.spin.started >= 0) f |= F.spinning;
  if (u.spikyUntil > b.t) f |= F.rolled;
  if (u.shield > EPS || u.amberUntil > b.t) f |= F.shielded;
  if (u.burrowUntil > b.t) f |= F.burrowed;
  if (u.empowerUntil > b.t) f |= F.empowered;
  if (u.pushedOut) f |= F.pushedOut;
  if (u.climbUntil > b.t) f |= F.climbing;
  if (u.weakAttacks > 0 && u.weakKind === 'blinded') f |= F.blinded;
  if (u.weakAttacks > 0 && u.weakKind === 'timid') f |= F.timid;
  if (u.confusedUntil > b.t) f |= F.confused;
  if (u.weakAttacks > 0 && u.weakKind === 'distracted') f |= F.distracted;
  if (u.toxicUntil > b.t) f |= F.toxic;
  if (u.guardRun) f |= F.guarding;
  return f;
}

/**
 * F32, I25 (Fabi, nach Runde 8): Giftstapel für den Tropfen an der Figur. Gift von Items (Bienenstich, Fingerhutblüte,
 * Fingerhuttee, Fingerhutkranz) zählt mit, jede Portion, die noch wirkt, als ein Stapel; sonst sähe man es nur am Wort „Gift“
 */
function poisonShown(u: Unit): number {
  return u.poison.stacks + u.dots.filter((d) => d.venom && d.ticksLeft > 0).length;
}

/** K46: Zustände für Echtzeit mit Pause (FrameFlag2) */
function flags2Of(b: Battle, u: Unit): number {
  const F = FrameFlag2;
  let f = 0;
  if (u.travel?.kind === 'beckon') f |= F.beckoned;
  if (u.travel?.kind === 'tunnel') f |= F.tunneling;
  if (u.travel?.kind === 'roll') f |= F.rolling;
  if (u.slide) f |= F.sliding;
  if (u.stuckUntil > b.t) f |= F.stuck;
  if (u.digUntil > b.t) f |= F.digging;
  if (u.kind !== 'tree' && inBush(b.setup.obstacles, u)) f |= F.hidden;
  if (u.windup) f |= F.windup;
  if (u.alive && ready(u)) f |= F.ready;
  if (u.swoop) f |= F.swooping;
  return f;
}

/** Läuft der Kampf mit den Bausteinen für Echtzeit? Dann hat jedes Bild ein neuntes Feld */
const realtime = (b: Battle) => !!(b.setup.commandMode || b.setup.projectiles || b.setup.telegraphs || b.setup.obstacles?.length);

function recordFrame(b: Battle): void {
  if (!b.recordFrames) return;
  const rt = realtime(b);
  b.frames.push({
    t: round(b.t),
    units: b.units.map((u) => {
      const row: Frame['units'][number] = [round(u.x), round(u.y), round(u.hp), Math.round(u.mut), flagsOf(b, u), poisonShown(u), u.alive ? u.targetUid ?? -1 : -1, round(u.shield)];
      if (rt) row.push(flags2Of(b, u));
      return row;
    }),
  });
}

/** Nusshagel (Fabi, Phase Kern): Abgeprallte Nüsse landen beim Freund und heilen ihn, wenn er noch steht */
function landBounces(b: Battle): void {
  if (!b.bounces.length) return;
  const due = b.bounces.filter((x) => b.t + EPS >= x.at);
  if (!due.length) return;
  b.bounces = b.bounces.filter((x) => b.t + EPS < x.at);
  for (const x of due) {
    const src = b.units[x.src]!;
    const mate = b.units[x.to]!;
    if (mate.alive) heal(b, src, mate, x.amount, x.via, x.from);
  }
}

/** Ein Takt. Reihenfolge fest, damit gleicher Seed gleichen Kampf ergibt. */
export function stepBattle(b: Battle): void {
  if (b.outcome) return;
  b.t += b.rules.tickSeconds;
  // K46: Befehle, deren Takt gekommen ist
  applyCommands(b);
  // K54: Kräftemessen kommt zum Starkregen dazu
  const rain = rainMultiplierAt(b.rules, b.t) * duelMultiplier(b);
  if (rain !== b.rainMultiplier) {
    b.rainMultiplier = rain;
    emit(b, { t: round(b.t), type: 'heavyRain', multiplier: rain });
  }
  stormChaos(b);
  landBounces(b);
  // K47: Geschosse im Flug
  updateShots(b);
  for (const u of b.units) if (u.alive) updateTimers(b, u);
  for (const u of b.units) {
    // K23 Feige: zurück aus der Flucht
    updateUnit(b, u);
  }
  separate(b);
  checkEnd(b);
  recordFrame(b);
}

export function summarize(b: Battle): BattleResult {
  const friends = b.units.filter((u) => u.kind === 'friend');
  // K23 Feige: Jeder Entkommene nimmt sein Stück vom Topf mit
  const enemyCount = b.units.filter((u) => u.side === 'enemy').length;
  const escaped = b.units.filter((u) => u.escaped && u.side === 'enemy').length;
  const fullPot = xpPot(b.rules, b.setup.fieldSlots, b.storm.xpFactor) * (b.setup.xpFactor ?? 1);
  const pot = fullPot * Math.max(0, 1 - (escaped / Math.max(1, enemyCount)) * b.rules.xp.escapePenalty);
  // F42 (Fabi, Phase Kern): Wer fällt, kommt verwundet zurück; Abschied nimmt nur, wer schon verwundet war
  const fell = (f: Unit) => !f.alive && !f.escaped;
  const farewell = (f: Unit) => fell(f) && (f.wounded || systemOn('firstFallFarewell'));
  // F15: Wer Abschied nimmt, bekommt nichts; der ganze Topf geht an die anderen. K21, Runde 8 (Fabi): Wer über den Rand
  // geschubst wurde, hat bis dahin mitgekämpft und bekommt seinen Anteil; F42: ebenso, wer verwundet zurückkommt
  const survivors = friends.filter((f) => !farewell(f));
  const shares = distributeXp(b.rules, pot, survivors.map((f) => f.stats), b.outcome ?? 'loss');
  const xpOf = new Map(survivors.map((f, i) => [f.uid, shares[i]!]));
  const summaries: FriendSummary[] = friends.map((f) => ({
    uid: f.uid,
    id: f.runId,
    name: f.name,
    species: f.defId,
    friendClass: f.friendClass!,
    maxHp: f.maxHp,
    hpEnd: round(f.hp),
    farewell: farewell(f),
    fell: fell(f),
    downBy: fell(f) ? downerOf(b, f) : null,
    pushedOut: f.pushedOut,
    ...(f.sporeUsed ? { sporeUsed: true } : {}),
    kniffUses: f.kniffUses,
    treeHpEnd: f.treeUid !== null ? round(Math.max(0, b.units[f.treeUid]!.hp)) : null,
    stats: { ...f.stats },
    xp: Math.round(xpOf.get(f.uid) ?? 0),
  }));
  // E6: Ein vertriebener Befallener ist befreit; I18: vertriebene Gegner lassen ihr Item fallen
  const downed = b.units.filter((u) => u.side === 'enemy' && !u.alive && !u.escaped && u.kind !== 'tree');
  const won = b.outcome === 'win';
  return {
    outcome: b.outcome ?? 'loss',
    duration: round(b.t),
    xpPot: pot,
    escaped,
    xpLostToEscapes: fullPot - pot,
    friends: summaries,
    freed: won ? downed.filter((u) => u.kind === 'infested').map((u) => ({ uid: u.uid, species: u.defId, friendClass: u.friendClass!, name: u.name, level: u.level })) : [],
    drops: won ? [...downed.flatMap((u) => u.items), ...b.units.flatMap((u) => (u.side === 'friend' ? u.loot : []))] : [],
    units: b.units,
    events: b.events,
    frames: b.frames,
  };
}

/** F42: Wer ihn zu Fall gebracht hat, für die Geschichte der Wunde */
function downerOf(b: Battle, f: Unit): string | null {
  const e = b.events.find((x) => x.type === 'down' && x.unit === f.uid);
  const by = e && e.type === 'down' && e.by !== null ? b.units[e.by] : undefined;
  return by ? by.name : null;
}

/** Ganzer Kampf auf einmal: für Harness, Tests und die Darstellung, die das Protokoll abspielt. */
export function runBattle(setup: BattleSetup, seed: number, options: BattleOptions = {}, rules: CombatRules = defaultRules): BattleResult {
  const b = createBattle(setup, seed, options, rules);
  while (!b.outcome) stepBattle(b);
  return summarize(b);
}
